import React, { useState, useEffect } from "react";
import { BaseDialog } from "../../shared/BaseDialog";
import { DynamicForm } from "@shared/components/DynamicForm";
import { Button } from "@shared/ui/button";
import { FormService, FormField } from "@shared/services/FormServices";
import { Info } from "lucide-react";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { toSnakeCase } from "@shared/utils/caseConverter";

interface FindReplaceTransformationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  targetField: string;
  fileData: string[];
  onApplyTransformation: (config: any) => void;
}

const FindReplaceTransformationDialog: React.FC<
  FindReplaceTransformationDialogProps
> = ({ isOpen, onClose, targetField, fileData, onApplyTransformation }) => {
  const [formData, setFormData] = useState({
    findText: "",
    replaceText: "",
    matchType: "exact",
    replaceScope: "all",
  });

  const [previewData, setPreviewData] = useState<{
    before: string[];
    after: string[];
  }>({
    before: [],
    after: [],
  });

  useEffect(() => {
    if (fileData) {
      const samples = fileData.filter((s) => s && s !== "...").slice(0, 2);
      setPreviewData({ before: samples, after: [] });
      updatePreview({ ...formData }, samples);
    }
  }, [fileData]);

  const fields: FormField[] = [
    {
      name: "findText",
      type: "text",
      label: "Find",
      placeholder: "Text to find",
      className: "col-span-1",
    },
    {
      name: "replaceText",
      type: "text",
      label: "Replace with",
      placeholder: "Replacement text",
      className: "col-span-1",
    },
    {
      name: "matchType",
      type: "radio",
      label: "Match type",
      options: [
        { value: "exact", label: "Exact match" },
        { value: "case_insensitive", label: "Case-insensitive" }, // Changed from "case-insensitive"
        { value: "regex", label: "Regular expression" },
        { value: "whole_words", label: "Whole words only" }, // Changed from "whole-words"
      ],
      defaultValue: "exact",
      className: "col-span-1",
    },
    {
      name: "replaceScope",
      type: "radio",
      label: "Replace",
      options: [
        { value: "first", label: "First occurrence" },
        { value: "all", label: "All occurrences" },
      ],
      defaultValue: "all",
      className: "col-span-1",
    },
  ];

  const form = FormService.createForm({
    fields,
    mode: "onChange",
    defaultValues: formData,
  });

  const handleFieldChange = (fieldName: string, value: any) => {
    const newFormData = { ...formData, [fieldName]: value };
    setFormData(newFormData);
    updatePreview(newFormData, previewData.before);
  };

  const updatePreview = (data: any, samples: string[]) => {
    if (!data.findText) {
      setPreviewData({ before: samples, after: [...samples] });
      return;
    }

    const results = samples.map((item) => {
      let result = item;
      const replacement = data.replaceText || "";

      switch (data.matchType) {
        case "exact":
          result =
            data.replaceScope === "first"
              ? item.replace(data.findText, replacement)
              : item.split(data.findText).join(replacement);
          break;
        case "case_insensitive":
          const flags = data.replaceScope === "first" ? "i" : "gi";
          const ciRegex = new RegExp(
            data.findText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
            flags
          );
          result = item.replace(ciRegex, replacement);
          break;
        case "regex":
          try {
            const regexFlags = data.replaceScope === "first" ? "" : "g";
            const regexPattern = new RegExp(data.findText, regexFlags);
            result = item.replace(regexPattern, replacement);
          } catch {
            result = item;
          }
          break;
        case "whole_words":
          const wordBoundaryRegex = new RegExp(
            `\\b${data.findText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`,
            data.replaceScope === "first" ? "" : "g"
          );
          result = item.replace(wordBoundaryRegex, replacement);
          break;
      }

      return result;
    });

    setPreviewData({ before: samples, after: results });
  };

  const handleApply = () => {
    if (!formData.findText) return;

    onApplyTransformation({
      targetField: toSnakeCase(targetField),
      transformation: {
        type: "find_replace",
        order: 1,
        config: {
          find: formData.findText,
          replace: formData.replaceText,
          matchType: formData.matchType,
          replaceMode: formData.replaceScope,
        },
      },
    });
    onClose();
  };

  return (
    <BaseDialog
      isOpen={isOpen}
      onClose={onClose}
      title="Find & Replace Configuration"
      className="max-w-4xl w-full max-h-[90vh] flex flex-col"
    >
      {/* Body */}
      <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
        {/* Info Banner */}
        <div className="flex items-start gap-3 text-sm text-gray-600 bg-blue-50 p-4 rounded-md">
          <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <strong>Find & Replace:</strong> Standardize data by replacing
            variations, fixing typos, or updating values.
          </div>
        </div>

        <DynamicForm
          fields={fields}
          form={form}
          config={{
            fields,
            layout: "vertical",
            showSubmitButton: false,
            showResetButton: false,
            classes: FIELD_CLASSES,
          }}
          onFieldChange={handleFieldChange}
          gridColumns={1}
        />

        {/* Preview */}
        <div className="border rounded-lg p-4 bg-gray-50">
          <h4 className="font-medium mb-3 text-gray-900">Preview</h4>
          <div className="grid grid-cols-2 gap-6">
            {/* Before */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-3">Before</p>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {previewData.before.map((item, idx) => (
                  <div
                    key={idx}
                    className="bg-white p-3 rounded-md border border-gray-200"
                  >
                    <span className="text-sm text-gray-800">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* After */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-3">After</p>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {previewData.after.map((item, idx) => (
                  <div
                    key={idx}
                    className="bg-white p-3 rounded-md border border-gray-200"
                  >
                    <span className="text-sm text-gray-800">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleApply} disabled={!formData.findText}>
            Apply Transformation
          </Button>
        </div>
      </div>
    </BaseDialog>
  );
};

export default FindReplaceTransformationDialog;
